import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { TeamFlag } from "@/components/TeamFlag";
import { toast } from "sonner";
import { Lock, Save, Trophy } from "lucide-react";
import type { MatchData } from "@/components/MatchCard";

interface GroupPrediction {
  id: string;
  group_name: string;
  first_team: string;
  second_team: string;
  points_awarded: number;
  user_id: string;
}
interface GroupResult {
  group_name: string;
  first_team: string;
  second_team: string;
}

interface Props {
  leagueId: string;
  tournamentId: string | null;
  userId: string;
  matches: MatchData[];
  lockMinutesBefore: number;
  positionPoints: number;
  advancingPoints: number;
}

export const GroupPredictionsTab = ({
  leagueId,
  tournamentId,
  userId,
  matches,
  lockMinutesBefore,
  positionPoints,
  advancingPoints,
}: Props) => {
  const { t, i18n } = useTranslation();
  const [myPreds, setMyPreds] = useState<Record<string, GroupPrediction>>({});
  const [results, setResults] = useState<Record<string, GroupResult>>({});
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState<Record<string, { first: string; second: string }>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [now, setNow] = useState<number>(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const formatCountdown = (ms: number) => {
    if (ms <= 0) return "0" + t("groups.seconds");
    const s = Math.floor(ms / 1000);
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    const parts: string[] = [];
    if (d) parts.push(`${d}${t("groups.days")}`);
    if (d || h) parts.push(`${h}${t("groups.hours")}`);
    if (d || h || m) parts.push(`${m}${t("groups.minutes")}`);
    parts.push(`${sec}${t("groups.seconds")}`);
    return parts.join(" ");
  };

  const firstKickoffPerGroup = useMemo(() => {
    const map: Record<string, number> = {};
    for (const mm of matches) {
      if (mm.phase !== "group" || !mm.group_name) continue;
      const t0 = new Date(mm.kickoff_at).getTime();
      if (map[mm.group_name] === undefined || t0 < map[mm.group_name]) map[mm.group_name] = t0;
    }
    return map;
  }, [matches]);

  const groups = useMemo(() => {
    const map: Record<string, Set<string>> = {};
    for (const m of matches) {
      if (m.phase !== "group" || !m.group_name) continue;
      if (!map[m.group_name]) map[m.group_name] = new Set();
      map[m.group_name].add(m.home_team);
      map[m.group_name].add(m.away_team);
    }
    return Object.entries(map)
      .map(([g, teams]) => ({ group: g, teams: Array.from(teams).sort() }))
      .sort((a, b) => a.group.localeCompare(b.group));
  }, [matches]);

  const groupLockedAt = useMemo(() => {
    const map: Record<string, number> = {};
    for (const m of matches) {
      if (m.phase !== "group" || !m.group_name) continue;
      const t = new Date(m.kickoff_at).getTime() - lockMinutesBefore * 60_000;
      if (map[m.group_name] === undefined || t < map[m.group_name]) map[m.group_name] = t;
    }
    return map;
  }, [matches, lockMinutesBefore]);

  const load = async () => {
    setLoading(true);
    const [{ data: preds }, { data: res }] = await Promise.all([
      supabase.from("group_predictions").select("*").eq("league_id", leagueId).eq("user_id", userId),
      tournamentId
        ? supabase.from("group_results").select("*").eq("tournament_id", tournamentId)
        : Promise.resolve({ data: [] as GroupResult[] }),
    ]);
    const m: Record<string, GroupPrediction> = {};
    (preds as GroupPrediction[] ?? []).forEach((p) => { m[p.group_name] = p; });
    setMyPreds(m);
    const r: Record<string, GroupResult> = {};
    (res as GroupResult[] ?? []).forEach((x) => { r[x.group_name] = x; });
    setResults(r);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [leagueId, tournamentId, userId]);

  const save = async (group: string) => {
    const draft = drafts[group];
    const existing = myPreds[group];
    const first = draft?.first ?? existing?.first_team ?? "";
    const second = draft?.second ?? existing?.second_team ?? "";
    if (!first || !second) { toast.error(t("groups.pickBoth")); return; }
    if (first === second) { toast.error(t("groups.distinctTeams")); return; }
    setSaving(group);
    const payload = {
      league_id: leagueId,
      tournament_id: tournamentId!,
      user_id: userId,
      group_name: group,
      first_team: first,
      second_team: second,
    };
    const { data, error } = existing
      ? await supabase.from("group_predictions").update(payload).eq("id", existing.id).select().single()
      : await supabase.from("group_predictions").insert(payload).select().single();
    setSaving(null);
    if (error) toast.error(error.message);
    else {
      toast.success(t("groups.saved"));
      if (data) {
        setMyPreds((prev) => ({ ...prev, [group]: data as GroupPrediction }));
      }
    }
  };

  if (loading) return <p className="text-sm text-muted-foreground">{t("common.loading")}</p>;
  if (!tournamentId) return <p className="text-sm text-muted-foreground">{t("groups.noTournament")}</p>;
  if (groups.length === 0) return <p className="text-sm text-muted-foreground">{t("groups.none")}</p>;

  return (
    <div className="space-y-4">
      <Card className="border-amber-300/40 bg-amber-50/40 dark:bg-amber-950/10">
        <CardContent className="p-4 text-sm text-amber-800 dark:text-amber-200 flex items-start gap-2">
          <Trophy className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <strong>{t("groups.howItWorks")}:</strong> {t("groups.scoringHint", { pos: positionPoints, adv: advancingPoints })}
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-3 sm:grid-cols-2">
        {groups.map(({ group, teams }) => {
          const existing = myPreds[group];
          const result = results[group];
          const lockAt = groupLockedAt[group];
          const firstAt = firstKickoffPerGroup[group];
          const locked = lockAt !== undefined && lockAt <= now;
          const msToLock = lockAt !== undefined ? lockAt - now : Infinity;
          const closeToLock = !locked && msToLock <= 60 * 60 * 1000; // < 1h
          const draft = drafts[group] ?? { first: existing?.first_team ?? "", second: existing?.second_team ?? "" };
          return (
            <Card key={group} className={`rounded-2xl border-border ${locked ? "opacity-90" : ""}`}>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center justify-between text-base">
                  <span>{t("groups.group")} {group}</span>
                  <div className="flex items-center gap-2">
                    {locked && (
                      <Badge variant="destructive" className="rounded-full text-[10px] gap-1">
                        <Lock className="h-3 w-3" />{t("groups.lockedShort")}
                      </Badge>
                    )}
                    {existing && (
                      <Badge variant="secondary" className="rounded-full text-xs">
                        +{existing.points_awarded} {t("common.points")}
                      </Badge>
                    )}
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {locked ? (
                  <div className="flex items-center gap-2 text-xs font-medium text-destructive bg-destructive/10 border border-destructive/20 rounded-md px-3 py-2">
                    <Lock className="h-3.5 w-3.5" />
                    <span>{t("groups.locked")}</span>
                  </div>
                ) : lockAt !== undefined ? (
                  <div className={`rounded-md px-3 py-2 text-xs space-y-0.5 border ${closeToLock ? "bg-amber-100/60 dark:bg-amber-950/30 border-amber-300/50 text-amber-900 dark:text-amber-100" : "bg-muted/30 border-border text-muted-foreground"}`}>
                    <div className="font-semibold">
                      {t("groups.locksIn")}: <span className="tabular-nums">{formatCountdown(msToLock)}</span>
                    </div>
                    {firstAt && (
                      <div className="opacity-80">
                        {t("groups.firstKickoff", { when: new Date(firstAt).toLocaleString(i18n.language) })}
                      </div>
                    )}
                  </div>
                ) : null}
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-muted-foreground">1. {t("groups.place")}</label>
                  <Select
                    value={draft.first}
                    onValueChange={(v) => setDrafts((d) => ({ ...d, [group]: { ...draft, first: v } }))}
                    disabled={locked}
                  >
                    <SelectTrigger className="h-10"><SelectValue placeholder={t("groups.pickTeam")} /></SelectTrigger>
                    <SelectContent>
                      {teams.map((tm) => (
                        <SelectItem key={tm} value={tm}>
                          <span className="inline-flex items-center gap-2"><TeamFlag team={tm} size="sm" />{tm}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-muted-foreground">2. {t("groups.place")}</label>
                  <Select
                    value={draft.second}
                    onValueChange={(v) => setDrafts((d) => ({ ...d, [group]: { ...draft, second: v } }))}
                    disabled={locked}
                  >
                    <SelectTrigger className="h-10"><SelectValue placeholder={t("groups.pickTeam")} /></SelectTrigger>
                    <SelectContent>
                      {teams.filter((tm) => tm !== draft.first).map((tm) => (
                        <SelectItem key={tm} value={tm}>
                          <span className="inline-flex items-center gap-2"><TeamFlag team={tm} size="sm" />{tm}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {result && (
                  <div className="rounded-xl bg-muted/40 px-3 py-2 text-xs space-y-1">
                    <div className="font-semibold text-muted-foreground">{t("groups.officialResult")}</div>
                    <div>1. <b>{result.first_team}</b> · 2. <b>{result.second_team}</b></div>
                  </div>
                )}

                {!locked && (
                  <Button
                    onClick={() => save(group)}
                    disabled={saving === group}
                    className="w-full h-10 rounded-full bg-gradient-hero text-primary-foreground shadow-gold border-0"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {existing ? t("groups.update") : t("common.save")}
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
